// Qué está haciendo el agente, dicho en criollo — ADR-2026-09-09-el-chat-dice-que-esta-haciendo.
//
// Mientras el modelo arma el input de una herramienta (los 18 prompts de un lote, las 300
// filas de un Excel) el chat no muestra nada: el texto ya terminó y la card todavía no
// existe. El usuario ve "Dale, las hago" y después silencio por medio minuto. Acá se
// traduce cada herramienta a una etiqueta que se entiende sin saber qué es una tool, y
// para las que se arman de a partes se cuenta el avance leyendo el JSON parcial.
//
// Todo puro: el motor llama estas funciones desde el stream y las UIs solo dibujan.

export const ETIQUETA_PENSANDO = 'Pensando…';
export const ETIQUETA_GENERICA = 'Trabajando…';

/** Qué ícono acompaña la etiqueta. Se deduce de la etiqueta, así el motor sigue
 *  exponiendo un string y las dos UIs (Chat y asistente) no cambian de contrato. */
export type TipoDeActividad =
  | 'pensar'
  | 'escribir'
  | 'mirar'
  | 'planilla'
  | 'pdf'
  | 'buscar'
  | 'guardar'
  | 'video'
  | 'audio'
  | 'catalogo'
  | 'pantalla';

interface Actividad {
  etiqueta: string;
  tipo: TipoDeActividad;
}

/**
 * Lo que queda escrito en la conversación cuando terminó: una línea por acción, en pasado
 * (ADR-2026-10-22-cada-accion-del-asistente-queda-en-una-linea). Sólo las que LEEN o miran algo,
 * que siempre pasan: las que escriben (publicar, subir fotos) pueden no haber hecho nada —publicar
 * la primera vez sólo avisa—, y eso lo cuenta el agente en su texto. Las que tienen card, la card.
 */
const HECHO: Record<string, string> = {
  abrir_web: 'Abrió la página',
  ver_productos: 'Miró tus productos',
  buscar_productos: 'Buscó en tus productos',
  ver_fotos: 'Miró tus fotos',
  ver_moodboard: 'Miró tus estilos',
  ver_elementos_de_marca: 'Miró tus elementos de marca',
  ver_planilla: 'Leyó la planilla',
  ver_pdf: 'Leyó el PDF',
  extraer_fotos_del_pdf: 'Sacó las fotos del PDF',
  ver_lanzamiento: 'Leyó el lanzamiento',
  ver_video: 'Analizó el video',
  extraer_frames: 'Sacó cuadros del video',
  traer_productos_de_la_tienda: 'Miró tu Tiendanube',
  ver_publicaciones_de_mercadolibre: 'Miró tu Mercado Libre',
  ver_calidad_en_mercadolibre: 'Miró la calidad en Mercado Libre',
  ver_metricas_de_instagram: 'Miró tus números de Instagram',
  ver_posts_de_instagram: 'Miró tus posts de Instagram',
  ver_publico_de_instagram: 'Miró quién te sigue en Instagram',
  ver_pantalla: 'Miró la pantalla',
  cargar_skill: 'Repasó cómo hacerlo',
};

/** La línea que deja una herramienta al terminar, o null si no deja ninguna. */
export function accionHecha(nombre: string): { texto: string; tipo: TipoDeActividad } | null {
  const texto = HECHO[nombre];
  if (!texto) return null;
  return { texto, tipo: POR_TOOL[nombre]?.tipo ?? 'mirar' };
}

/**
 * Herramientas del cliente (`V2_AGENT_TOOLS` + las del Chat). La etiqueta vale tanto
 * mientras el modelo escribe el input como mientras la herramienta corre después.
 */
const POR_TOOL: Record<string, Actividad> = {
  generate_image: { etiqueta: 'Armando el prompt de la imagen…', tipo: 'escribir' },
  generar_lote_imagenes: { etiqueta: 'Armando los prompts de las imágenes…', tipo: 'escribir' },
  generate_video: { etiqueta: 'Armando el video…', tipo: 'video' },
  generate_audio: { etiqueta: 'Preparando el audio…', tipo: 'audio' },
  pick_product: { etiqueta: 'Buscando en tu catálogo…', tipo: 'catalogo' },
  pick_creacion: { etiqueta: 'Buscando la pieza…', tipo: 'buscar' },
  ver_productos: { etiqueta: 'Mirando tus productos…', tipo: 'mirar' },
  buscar_productos: { etiqueta: 'Buscando en tus productos…', tipo: 'mirar' },
  ver_fotos: { etiqueta: 'Mirando tus fotos…', tipo: 'mirar' },
  abrir_web: { etiqueta: 'Abriendo la página…', tipo: 'buscar' },
  ver_moodboard: { etiqueta: 'Mirando tus estilos…', tipo: 'mirar' },
  // ADR-2026-10-20-el-agente-usa-los-elementos-de-marca
  ver_elementos_de_marca: { etiqueta: 'Mirando tus elementos de marca…', tipo: 'mirar' },
  guardar_en_elementos_de_marca: { etiqueta: 'Guardando en tus elementos de marca…', tipo: 'catalogo' },
  editar_elementos_de_marca: { etiqueta: 'Ordenando tus elementos de marca…', tipo: 'catalogo' },
  // Copiar fotos a una carpeta es una bajada y una subida por foto: sin etiqueta el chat
  // se queda mudo varios segundos (ADR-2026-09-13-el-agente-arma-moodboards).
  agregar_al_moodboard: { etiqueta: 'Guardando en tus estilos…', tipo: 'catalogo' },
  mandar_a_instagram: { etiqueta: 'Agregando al calendario de Instagram…', tipo: 'catalogo' },
  // Tiendanube: todas consultan la tienda o le escriben, y eso tarda. Sin etiqueta
  // propia el chat decía "Trabajando…" mientras subía fotos a la tienda de alguien.
  traer_productos_de_la_tienda: { etiqueta: 'Mirando tu Tiendanube…', tipo: 'catalogo' },
  subir_fotos_a_la_tienda: { etiqueta: 'Subiendo las fotos a tu tienda…', tipo: 'guardar' },
  crear_producto_en_la_tienda: { etiqueta: 'Creando el producto en tu tienda…', tipo: 'guardar' },
  publicar_lanzamiento: { etiqueta: 'Publicando el lanzamiento…', tipo: 'guardar' },
  reemplazar_fotos_del_producto: { etiqueta: 'Cambiando las fotos del producto…', tipo: 'guardar' },
  cambiar_datos_en_la_tienda: { etiqueta: 'Actualizando el producto en tu tienda…', tipo: 'guardar' },
  conectar_tienda: { etiqueta: 'Preparando la conexión con Tiendanube…', tipo: 'pantalla' },
  // Instagram: cada una consulta a Meta y tarda unos segundos (varias llamadas).
  ver_metricas_de_instagram: { etiqueta: 'Mirando tus números de Instagram…', tipo: 'mirar' },
  ver_posts_de_instagram: { etiqueta: 'Mirando tus posts de Instagram…', tipo: 'mirar' },
  ver_publico_de_instagram: { etiqueta: 'Mirando quién te sigue en Instagram…', tipo: 'mirar' },
  conectar_instagram: { etiqueta: 'Preparando la conexión con Instagram…', tipo: 'pantalla' },
  conectar_anuncios: { etiqueta: 'Preparando la conexión con tus anuncios…', tipo: 'pantalla' },
  // Mercado Libre (ADR-2026-09-28-mercadolibre-en-el-chat).
  ver_publicaciones_de_mercadolibre: { etiqueta: 'Mirando tu Mercado Libre…', tipo: 'catalogo' },
  traer_de_mercadolibre: { etiqueta: 'Trayendo de tu Mercado Libre…', tipo: 'catalogo' },
  subir_fotos_a_mercadolibre: { etiqueta: 'Subiendo las fotos a Mercado Libre…', tipo: 'guardar' },
  cambiar_precio_o_descripcion_en_mercadolibre: { etiqueta: 'Actualizando tu publicación…', tipo: 'guardar' },
  ver_calidad_en_mercadolibre: { etiqueta: 'Mirando la calidad en Mercado Libre…', tipo: 'mirar' },
  conectar_mercadolibre: { etiqueta: 'Preparando la conexión con Mercado Libre…', tipo: 'pantalla' },
  editar_moodboard: { etiqueta: 'Actualizando tus estilos…', tipo: 'catalogo' },
  ver_planilla: { etiqueta: 'Leyendo la planilla…', tipo: 'planilla' },
  ver_pdf: { etiqueta: 'Leyendo el PDF…', tipo: 'pdf' },
  extraer_fotos_del_pdf: { etiqueta: 'Sacando las fotos del PDF…', tipo: 'pdf' },
  guardar_datos_del_producto: { etiqueta: 'Guardando los datos del producto…', tipo: 'guardar' },
  ver_lanzamiento: { etiqueta: 'Leyendo el lanzamiento…', tipo: 'mirar' },
  ver_video: { etiqueta: 'Analizando el video…', tipo: 'video' },
  extraer_frames: { etiqueta: 'Sacando cuadros del video…', tipo: 'video' },
  finalizar_borrador_de_video: { etiqueta: 'Pasando el video a 1080p…', tipo: 'video' },
  crear_planilla: { etiqueta: 'Armando el Excel…', tipo: 'planilla' },
  crear_pdf: { etiqueta: 'Armando el PDF…', tipo: 'pdf' },
  editar_lanzamiento: { etiqueta: 'Preparando los cambios del lanzamiento…', tipo: 'catalogo' },
  agregar_a_lanzamiento: { etiqueta: 'Preparando los productos para el lanzamiento…', tipo: 'catalogo' },
  agregar_productos: { etiqueta: 'Organizando las fotos en productos…', tipo: 'catalogo' },
  mostrar_guardado: { etiqueta: 'Buscando lo que tenés guardado…', tipo: 'buscar' },
  show_recent_media: { etiqueta: 'Buscando lo último que generaste…', tipo: 'buscar' },
  cargar_skill: { etiqueta: 'Repasando cómo hacerlo…', tipo: 'pensar' },
  guardar_skill: { etiqueta: 'Guardando cómo querés que lo haga…', tipo: 'guardar' },
  save_memory: { etiqueta: 'Guardando en memoria…', tipo: 'guardar' },
  activar_investigacion: { etiqueta: 'Pidiendo permiso para investigar…', tipo: 'buscar' },
  contact_support: { etiqueta: 'Preparando el mensaje para soporte…', tipo: 'escribir' },
  mostrar_respuesta_grafica: { etiqueta: 'Armando el gráfico…', tipo: 'escribir' },
  sugerir_acciones: { etiqueta: 'Pensando qué sigue…', tipo: 'pensar' },
  preguntar: { etiqueta: 'Armando las preguntas…', tipo: 'escribir' },
  // Las de pantalla (ADR-2026-09-10-el-agente-toca-la-pantalla).
  ver_pantalla: { etiqueta: 'Mirando la pantalla…', tipo: 'mirar' },
  configurar_pantalla: { etiqueta: 'Configurando la pantalla…', tipo: 'pantalla' },
  abrir_modo: { etiqueta: 'Abriendo el modo…', tipo: 'pantalla' },
  // ADR-2026-09-13-el-agente-configura-las-preferencias. Sin esto mostraba la etiqueta
  // genérica ("Pensando…") mientras cambia un default, que no dice nada de lo que pasó.
  configurar_preferencias: {
    etiqueta: 'Cambiando tus preferencias…',
    tipo: 'pantalla',
  },
  // ADR-2026-10-21-el-agente-ve-crea-y-usa-los-formatos-personalizados.
  crear_formato_personalizado: { etiqueta: 'Guardando el formato…', tipo: 'guardar' },
};

/** Las server-side: la búsqueda web de Anthropic y los scrapers de Apify vía MCP. */
function actividadServidor(blockType: string, nombre: string): Actividad {
  if (blockType === 'server_tool_use') return { etiqueta: 'Buscando en la web…', tipo: 'buscar' };
  const n = (nombre || '').toLowerCase();
  // El MCP oficial de anuncios de Meta: todas sus herramientas empiezan con `ads_`
  // (ADR-2026-09-24-anuncios-de-meta-en-el-chat). Va antes que los scrapers.
  if (n.startsWith('ads_')) return { etiqueta: 'Mirando tus anuncios de Meta…', tipo: 'mirar' };
  if (n.includes('instagram')) return { etiqueta: 'Analizando Instagram…', tipo: 'buscar' };
  if (n.includes('ads-library') || n.includes('facebook-ad')) {
    return { etiqueta: 'Revisando anuncios de Facebook…', tipo: 'buscar' };
  }
  if (n.includes('facebook')) return { etiqueta: 'Analizando Facebook…', tipo: 'buscar' };
  if (n.includes('places') || n.includes('google')) {
    return { etiqueta: 'Buscando lugares en Google…', tipo: 'buscar' };
  }
  if (n.includes('commerce')) return { etiqueta: 'Buscando productos…', tipo: 'buscar' };
  return { etiqueta: 'Buscando datos…', tipo: 'buscar' };
}

function actividadDe(nombre: string, blockType = 'tool_use'): Actividad {
  if (blockType === 'server_tool_use' || blockType === 'mcp_tool_use') {
    return actividadServidor(blockType, nombre);
  }
  return POR_TOOL[nombre] ?? { etiqueta: ETIQUETA_GENERICA, tipo: 'pensar' };
}

/** La etiqueta de una herramienta recién arrancada (o que está corriendo). */
export function etiquetaDeTool(nombre: string, blockType = 'tool_use'): string {
  return actividadDe(nombre, blockType).etiqueta;
}

// ── Mientras lee lo que trajo una herramienta ───────────────────────────────

/**
 * Las herramientas que TRAEN algo para que el modelo lea (fotos, una skill, la pantalla,
 * un archivo), en orden de prioridad: si en la misma ronda corrieron varias, la etiqueta
 * es la de la que más pesa leer (las fotos antes que una skill).
 *
 * Después de una de éstas, la ronda siguiente arranca con el modelo leyendo lo que trajo,
 * y esa espera es la más larga del turno: con "Pensando…" el chat parecía parado
 * (ADR-2026-10-15-el-agente-dice-que-esta-leyendo). Las demás herramientas HACEN algo
 * (guardar, armar, cambiar) y lo que sigue es pensar de verdad.
 */
const LECTURAS: readonly string[] = [
  'ver_productos',
  'buscar_productos',
  'abrir_web',
  'ver_fotos',
  'ver_moodboard',
  'ver_elementos_de_marca',
  'ver_video',
  'extraer_frames',
  'ver_planilla',
  'ver_pdf',
  'extraer_fotos_del_pdf',
  'ver_lanzamiento',
  'traer_productos_de_la_tienda',
  'ver_publicaciones_de_mercadolibre',
  'ver_calidad_en_mercadolibre',
  'ver_metricas_de_instagram',
  'ver_posts_de_instagram',
  'ver_publico_de_instagram',
  'ver_pantalla',
  'cargar_skill',
];

/**
 * La etiqueta con la que arranca la ronda que sigue a estas herramientas: la de la
 * lectura que más pesa, o "Pensando…" si ninguna trajo algo para leer.
 */
export function etiquetaAlLeerResultados(nombres: readonly string[]): string {
  const lectura = LECTURAS.find((n) => nombres.includes(n));
  return lectura ? etiquetaDeTool(lectura) : ETIQUETA_PENSANDO;
}

// ── Avance mientras se arma el input ────────────────────────────────────────

/** Cuántas veces aparece la clave en el JSON parcial: cada aparición es una parte
 *  que el modelo ya empezó. */
const cuenta = (json: string, re: RegExp): number => (json.match(re) ?? []).length;

/**
 * Herramientas cuyo input se arma de a partes contables. La cuenta se hace sobre el
 * JSON parcial que el stream va acumulando: no hace falta parsearlo (está incompleto).
 */
const AVANCE: Record<string, (json: string) => string | null> = {
  generar_lote_imagenes: (json) => {
    const n = cuenta(json, /"prompt"\s*:/g);
    return n > 0 ? `Escribiendo el prompt ${n}…` : null;
  },
  crear_planilla: (json) => {
    // Cada fila es un array de strings; los límites entre filas son `],[`.
    const filas = cuenta(json, /\]\s*,\s*\[/g);
    return filas >= 5 ? `Armando el Excel… ${filas} filas` : null;
  },
  crear_pdf: (json) => {
    const partes = cuenta(json, /"tipo"\s*:/g);
    return partes >= 2 ? `Armando el PDF… ${partes} partes` : null;
  },
  editar_lanzamiento: (json) => {
    const n = cuenta(json, /"campo"\s*:/g);
    return n >= 2 ? `Preparando los cambios del lanzamiento… ${n}` : null;
  },
};

/**
 * La etiqueta con el avance, para mostrar mientras llega el input. Si la herramienta no
 * tiene partes contables (o todavía no empezó ninguna), la etiqueta de siempre.
 */
export function etiquetaConAvance(nombre: string, jsonParcial: string): string {
  const conAvance = AVANCE[nombre]?.(jsonParcial ?? '');
  return conAvance ?? etiquetaDeTool(nombre);
}

// ── Qué ícono va con cada etiqueta ──────────────────────────────────────────

const TIPO_POR_ETIQUETA: Map<string, TipoDeActividad> = new Map(
  Object.values(POR_TOOL).map((a) => [a.etiqueta, a.tipo]),
);

/** El tipo (ícono) de una etiqueta. Las de avance heredan el de su herramienta;
 *  las que no se reconocen, "pensar". */
export function tipoDeActividad(etiqueta: string | null | undefined): TipoDeActividad {
  if (!etiqueta) return 'pensar';
  const directo = TIPO_POR_ETIQUETA.get(etiqueta);
  if (directo) return directo;
  if (/^Escribiendo el prompt/.test(etiqueta)) return 'escribir';
  if (/^Armando el Excel/.test(etiqueta)) return 'planilla';
  if (/^Armando el PDF/.test(etiqueta)) return 'pdf';
  if (/^Preparando los cambios/.test(etiqueta)) return 'catalogo';
  if (/^(Buscando|Analizando|Revisando)/.test(etiqueta)) return 'buscar';
  // Las herramientas propias del Estudio de Video traen su etiqueta consigo
  // (ADR-2026-09-11-el-director-del-estudio-se-funde-en-el-agente): el verbo dice el ícono.
  if (/^(Leyendo|Mirando)/.test(etiqueta)) return 'mirar';
  if (/^Transcribiendo/.test(etiqueta)) return 'audio';
  if (/^Guardando/.test(etiqueta)) return 'guardar';
  if (/^(Cortando|Moviendo|Agregando|Insertando|Borrando|Ajustando|Cargando|Abriendo|Renombrando|Silenciando|Traduciendo|Corrigiendo|Importando|Sacando|Pidiendo|Cambiando|Dividiendo|Recortando|Señalando)/.test(etiqueta)) {
    return 'pantalla';
  }
  return 'pensar';
}

// ── ¿Trabaja o me espera? ───────────────────────────────────────────────────

/**
 * ¿Esa card pendiente está esperando que el usuario haga algo?
 * (ADR-2026-09-14-el-lote-abierto-no-es-una-card-que-espera)
 *
 * Casi todas sí: una propuesta de generación, un selector de producto, una confirmación.
 * Mientras una de ésas está en pantalla el agente no hace nada —espera un click—, y por
 * eso la mascota se queda quieta y sin etiqueta.
 *
 * El LOTE ABIERTO es la excepción y rompía la regla: queda `pending` a propósito entre
 * una llamada y la siguiente mientras el agente le suma ítems, y la propia card dice
 * "cuando termine de armarlo vas a ver el botón". Tratarlo como una card que espera
 * dejaba al chat mudo y a la mascota congelada justo durante lo más largo del turno:
 * escribir treinta prompts.
 */
export function esperaAlUsuario(
  card: { kind: string; input?: unknown } | null | undefined,
): boolean {
  if (!card) return false;
  const input = (card.input ?? {}) as { abierto?: unknown };
  if (card.kind === 'generar_lote_imagenes' && input.abierto === true) return false;
  return true;
}

/**
 * Herramientas que NO muestran una card: mientras corren, lo único visible es la
 * etiqueta. Para las demás, la card que aparece es el indicador, y la etiqueta se apaga
 * para no decir "armando el prompt" arriba de una card que ya lo muestra.
 */
export const TOOLS_SIN_CARD = new Set([
  'abrir_web',
  'ver_productos',
  'buscar_productos',
  'ver_fotos',
  'ver_moodboard',
  'agregar_al_moodboard',
  'editar_moodboard',
  'ver_elementos_de_marca',
  'guardar_en_elementos_de_marca',
  'editar_elementos_de_marca',
  'mandar_a_instagram',
  'ver_planilla',
  'ver_pdf',
  'extraer_fotos_del_pdf',
  'guardar_datos_del_producto',
  'ver_lanzamiento',
  'ver_video',
  'extraer_frames',
  'finalizar_borrador_de_video',
  'cargar_skill',
  'guardar_skill',
  'save_memory',
  'mostrar_respuesta_grafica',
  'sugerir_acciones',
  'ver_pantalla',
  'configurar_pantalla',
  'abrir_modo',
  'crear_formato_personalizado',
  // Mercado Libre: silenciosas, así la etiqueta se ve también mientras corren (esperan un job).
  'ver_publicaciones_de_mercadolibre',
  'traer_de_mercadolibre',
  'subir_fotos_a_mercadolibre',
  'cambiar_precio_o_descripcion_en_mercadolibre',
  'ver_calidad_en_mercadolibre',
]);
